import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ClipboardList,
  FileText,
  Sparkles,
  ShieldCheck,
  Clock,
  PhoneCall,
} from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";

const STEPS = [
  {
    num: "01",
    title: "Free Site Survey",
    duration: "1–3 days from first call",
    body: "We visit your premises anywhere in Ireland and walk it with you. We measure square footage, photograph problem areas, ask about your team's daily patterns, and identify safety, access, and timing constraints.",
    bullets: [
      "Same-week site visits across Ireland",
      "Drawings or floor plans reviewed in advance if available",
      "Confidentiality agreements (NDA) available on request.",
      "We arrive in branded vehicles and full AGS uniform",
    ],
    icon: ClipboardList,
  },
  {
    num: "02",
    title: "Tailored Quotation",
    duration: "Within 24 hours of survey",
    body: "You receive a transparent, itemised proposal in your inbox. Hours, scope, frequency, consumables, products, and total price — all line by line. Transparent pricing with no hidden costs or unnecessary commitments.",
    bullets: [
      "Itemised pricing per area / per frequency",
      "Recommended schedule built around your operations",
      "Optional add-ons clearly priced (deep clean, windows, carpets)",
      "Plain-English contract — readable in 5 minutes",
    ],
    icon: FileText,
  },
  {
    num: "03",
    title: "Professional Cleaning Services",
    duration: "Day 1 onwards",
    body: "Our managed family-led team delivers flawless standards with zero disruption. Every operative wears the branded AGS navy vest, follows the site-specific RAMS, and logs every chemical they touch via COSHH.",
    bullets: [
      "Same crew on every visit — no faceless agency rotation",
      "RAMS issued before the first shift",
      "Industrial-grade equipment supplied by us",
      "Eco-certified chemicals, COSHH-logged with SDS",
    ],
    icon: Sparkles,
  },
  {
    num: "04",
    title: "Quality Check & Sign-Off",
    duration: "Continuous, with monthly audits",
    body: "We personally inspect the results with you. Our on-site foreman runs daily checks, our service supervisor runs weekly audits, and our mobile manager runs monthly client check-ins — so anything that drifts gets caught early. Our family guarantee means we fix anything not 100% within 24 hours, free of charge.",
    bullets: [
      "Daily on-site foreman check-list + photo evidence",
      "Weekly supervisor walk-through across the site",
      "Monthly mobile manager audit with you",
      "24-hour fix guarantee on anything reported",
      "Quarterly service-review meeting included",
    ],
    icon: ShieldCheck,
  },
];

const PRINCIPLES = [
  {
    k: "Same crew every visit",
    v: "We don't rotate strangers through your space. Your dedicated AGS team learns your premises and your people.",
  },
  {
    k: "On-time, every time",
    v: "Our supervisors use mobile GPS check-ins so we always know who's on site and when. Late starts get flagged automatically.",
  },
  {
    k: "Documented, not promised",
    v: "Every chemical, every method, every audit logged with date, operative, and outcome. Cleaning records maintained from day one.",
  },
  {
    k: "Family-fix guarantee",
    v: "If anything is ever less than perfect, we re-clean within 24 hours — no quibble, no charge. Personal promise from Gazi.",
  },
];

export default function ProcessPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="process-page" className="bg-white text-[#050B14]">
      <Navigation />

      <main>
        <section className="relative bg-[#F4F8FC] pt-40 md:pt-48 pb-16 md:pb-24 border-b border-[#E5EBF2] overflow-hidden">
          <div className="relative container-ags">
            <Link
              to="/"
              className="group inline-flex items-center gap-2 text-sm font-medium text-[#0E2A57] hover:text-[#2BB6E5] transition-colors mb-8"
            >
              <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
              Back to home
            </Link>

            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-16 bg-[#2BB6E5]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                06 — How It Works
              </span>
            </div>
            <h1 className="font-heading font-extrabold text-5xl md:text-7xl leading-[1.0] tracking-[-0.04em] text-[#0E2A57] max-w-4xl">
              From first call to{" "}
              <span className="italic-display font-normal text-[#2BB6E5]">
                flawless finish
              </span>{" "}
              — in four steps.
            </h1>
            <p className="mt-8 text-[#475569] text-lg md:text-xl leading-relaxed max-w-2xl">
              Our process is built for facility managers who don't have time
              for surprises. Transparent scoping, a clear quote, a managed
              crew, and quality you can sign off on.
            </p>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-white border border-[#E5EBF2]">
              {[
                { k: "24h", v: "Quotation turnaround" },
                { k: "Same crew", v: "Every visit, same faces" },
                { k: "Monthly", v: "Manager audits" },
                { k: "24h", v: "Fix guarantee" },
              ].map((s) => (
                <div key={s.k} className="p-5 md:p-6">
                  <div className="font-heading font-extrabold text-2xl md:text-3xl text-[#0E2A57] tracking-[-0.035em]">
                    {s.k}
                  </div>
                  <div className="text-xs text-[#737373] mt-1.5">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="bg-white py-24 md:py-36">
          <div className="container-ags space-y-16 md:space-y-20">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              const reverse = i % 2 === 1;
              return (
                <article
                  key={step.num}
                  data-testid={`process-step-${step.num}`}
                  className={`grid md:grid-cols-12 gap-10 md:gap-16 items-center ${
                    reverse ? "md:[&>*:first-child]:order-2" : ""
                  }`}
                >
                  <div className="md:col-span-5">
                    <div className="relative aspect-square bg-[#0E2A57] flex items-center justify-center overflow-hidden">
                      <div
                        aria-hidden
                        className="absolute inset-0 opacity-25"
                        style={{
                          backgroundImage:
                            "radial-gradient(circle at 30% 30%, rgba(43,182,229,0.5), transparent 55%), radial-gradient(circle at 75% 75%, rgba(190,231,245,0.3), transparent 55%)",
                        }}
                      />
                      <div className="relative text-center">
                        <Icon size={80} strokeWidth={1.1} className="text-[#BEE7F5] mx-auto" />
                        <div className="mt-6 italic-display font-normal text-[10rem] leading-none text-white/15">
                          {step.num}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-7">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="font-heading font-black text-[#2BB6E5] text-xl">
                        {step.num}
                      </span>
                      <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                        <Clock size={12} />
                        {step.duration}
                      </span>
                    </div>
                    <h2 className="font-heading font-extrabold text-3xl md:text-5xl leading-[1.05] tracking-[-0.035em] text-[#0E2A57]">
                      {step.title}
                    </h2>
                    <p className="mt-5 text-[#475569] text-lg leading-relaxed">
                      {step.body}
                    </p>
                    <ul className="mt-8 divide-y divide-[#E5EBF2] border-t border-b border-[#E5EBF2]">
                      {step.bullets.map((b) => (
                        <li key={b} className="py-3.5 flex items-start gap-3 text-[#050B14] text-base">
                          <span className="text-[#2BB6E5] font-bold mt-0.5">→</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* Principles */}
        <section className="bg-[#0E2A57] text-white py-24 md:py-36 relative overflow-hidden">
          <div className="relative container-ags">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-16 bg-[#2BB6E5]" />
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#BEE7F5] font-medium">
                  Operating principles
                </span>
              </div>
              <h2 className="font-heading font-extrabold text-3xl md:text-5xl leading-[1.05] tracking-[-0.035em] text-white">
                Four rules we run our service by.
              </h2>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/15 border border-white/15">
              {PRINCIPLES.map((p) => (
                <div key={p.k} className="bg-[#0E2A57] p-7 md:p-9 hover:bg-[#081A37] transition-colors">
                  <div className="font-heading font-bold text-xl text-white">{p.k}</div>
                  <p className="mt-3 text-white/75 text-base leading-relaxed">{p.v}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-white py-16 md:py-24 border-t border-[#E5EBF2]">
          <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
              Ready when you are
            </span>
            <h2 className="mt-4 font-heading font-extrabold text-4xl md:text-5xl leading-[1.05] tracking-[-0.04em] text-[#0E2A57]">
              Start with a free site survey.
            </h2>
            <p className="mt-6 text-[#475569] text-lg leading-relaxed max-w-xl mx-auto">
              We'll visit, scope the work, and send you a transparent quote
              within 24 hours. No obligation, no chasing.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/#quote"
                className="group inline-flex items-center gap-3 bg-[#0E2A57] text-white hover:bg-[#081A37] px-10 py-5 font-semibold tracking-wide transition-colors"
              >
                Request a Quote
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 border-2 border-[#0E2A57] text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-10 py-5 font-semibold tracking-wide transition-all"
              >
                <PhoneCall size={16} />
                Talk to us first
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
